import { useCallback, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { useGetAdminCatalog } from '@/hooks/queries/nodes/use-get-admin-catalog';
import { useGetOrgNodeAccessConfig } from '@/hooks/queries/nodes/use-get-org-node-access-config';
import { useUpdateCapitolConfig } from '@/hooks/mutations/nodes/use-update-capitol-config';
import { NodeCatalogList } from '@/components/node-catalog-list';

interface OrgNodesToolsTabProps {
  orgId: string;
}

export const OrgNodesToolsTab = ({ orgId }: OrgNodesToolsTabProps) => {
  const { data: catalog = [], isLoading: catalogLoading } =
    useGetAdminCatalog();
  const { data: configData, isLoading: configLoading } =
    useGetOrgNodeAccessConfig(orgId);
  const { mutateAsync: saveConfig } = useUpdateCapitolConfig();

  const loading = catalogLoading || configLoading;
  const config = useMemo(() => configData?.items || {}, [configData]);
  const hasConfig = Object.keys(config).length > 0;

  const getOriginalValue = useCallback(
    (nodeId: string): boolean => {
      if (!hasConfig) return false;
      return config[nodeId]?.allowed_by_capitol ?? false;
    },
    [config, hasConfig],
  );

  const onSave = useCallback(
    async (changes: Record<string, boolean>) => {
      await saveConfig({ orgId, items: changes });
    },
    [orgId, saveConfig],
  );

  if (loading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <Loader2 className='size-6 animate-spin text-muted-foreground' />
        <span className='ml-2 text-muted-foreground'>
          Loading nodes and tools...
        </span>
      </div>
    );
  }

  const infoBanner = !hasConfig ? (
    <div className='rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800'>
      This organization has not been configured yet. No nodes or tools are
      currently visible to users. Enable items below to start configuring.
    </div>
  ) : undefined;

  return (
    <NodeCatalogList
      items={catalog}
      getOriginalValue={getOriginalValue}
      onSave={onSave}
      successMessage='Node configuration saved successfully'
      infoBanner={infoBanner}
    />
  );
};
